import { IconAngleLeft, IconNavigator } from '@maia/icons';
import {
  Button,
  cardStyles,
  FinalPriceSection,
  SectionWithHeader,
  Spec,
  SpecList,
  TextButton,
  Typography,
} from '@maia/ui';
import { formatToDateInput } from '@maia/utils';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { isNil } from 'es-toolkit';
import { twMerge } from 'tailwind-merge';
import { getCustomerOptions } from '../../../../features/customer/customer/queryOptions';
import { getProductsOptions } from '../../../../features/edit-insurance/get-products/queryOptionts';
import { useAppForm } from '../../../../features/form/dynamic-form/dynamic-form';
import { CustomerInformation } from '../../../../features/shared/customer/customer-information';
import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { service } from '@maia/cart';
import { getMutationCartOptions } from '../../../../features/edit-insurance/get-cart/queryOptionts';

export const Route = createFileRoute(
  '/_authenticated/edit-insurance/$customer/cart/$cart/summary',
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { customer: customerGuid, cart },
  }) => {
    const mutationCart = await queryClient.ensureQueryData(
      getMutationCartOptions({ cartGuid: cart, customerGuid }),
    );
    const customer = await queryClient.ensureQueryData(
      getCustomerOptions(customerGuid),
    );
    const products = await queryClient.ensureQueryData(
      getProductsOptions({
        cartGuid: cart,
        customerGuid: customerGuid,
      }),
    );
    return { customer, product: products[0], mutationCart };
  },
});

const DEFAULT_DATE_VALUE = new Date(Date.now() + 24 * 60 * 60 * 1000);

function RouteComponent() {
  const { cart } = Route.useParams();
  const { customer, product, mutationCart } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const router = useRouter();

  const { mutate, isPending } = useMutation({
    mutationFn: async (
      effectiveDate: Parameters<
        typeof service.submitMutation
      >[0]['effectiveDate'],
    ) => {
      service.submitMutation({
        cartGuid: cart,
        customerGuid: customer.guid,
        effectiveDate,
      });
    },
    onSuccess: () => {
      navigate({
        to: '/edit-insurance/$customer/cart/$cart/success',
      });
    },
  });

  const form = useAppForm({
    defaultValues: {
      effectiveDate: DEFAULT_DATE_VALUE,
    },
    onSubmit: ({ value }) => {
      const effectiveDate = formatToDateInput(value.effectiveDate);
      mutate(effectiveDate);
    },
  });

  const addons = useMemo(() => {
    if (product?.addOns?.length === 0) return [];
    return product?.addOns?.flatMap((addon) => {
      if (addon.answer.value === 'N') return [];

      const value = addon.options.find(
        (o) => o.value === addon.answer.value,
      )?.label;
      return {
        icon: addon.icon,
        title: addon.title ?? addon.phrasing,
        value,
        price: addon.price?.premiumAfterTax,
        isHalf: true,
      };
    });
  }, [product]);

  const specs = useMemo(
    () =>
      product?.object?.specs.flatMap((spec) => {
        if (isNil(spec.answer.value) || isNil(spec.description)) return [];
        return {
          title: spec.description,
          value: spec.answer.value?.toString() ?? '',
          isHalf: true,
        };
      }),
    [product],
  );

  return (
    <div className="layout-sidebar">
      <div className="area-page-content content-grid">
        <div className="-ml-32 -mr-32 col-span-full">
          <div className="area-page-header px-32 flex items-center justify-between">
            <TextButton
              as="link"
              onPress={() => router.history.back()}
              intent="standard"
              size="tiny"
              iconLeft={IconAngleLeft}
            >
              Terug
            </TextButton>
          </div>
        </div>
        <div className="col-span-full">
          <Typography
            variant="display6"
            weight="semibold"
            className="inline-flex mb-48"
          >
            Controle & ingangsdatum
          </Typography>
          <div className="flex flex-col gap-y-32">
            {!!specs?.length && (
              <SectionWithHeader
                title="Object details"
                className="col-span-full"
                key={`object-details-${product?.guid}`}
              >
                <div
                  className={twMerge(
                    cardStyles({ variant: 'outlined' }),
                    'p-20',
                  )}
                >
                  <SpecList items={specs} />
                </div>
              </SectionWithHeader>
            )}
            {!!addons?.length && (
              <SectionWithHeader
                title="Addons"
                className="col-span-full"
                key={`addons-${product?.guid}`}
              >
                <div
                  className={twMerge(
                    cardStyles({ variant: 'outlined' }),
                    'p-20',
                  )}
                >
                  <SpecList>
                    {addons.map((addon) => (
                      <Spec title={addon.title} value={addon.value} />
                    ))}
                  </SpecList>
                </div>
              </SectionWithHeader>
            )}

            <FinalPriceSection
              totalPrice={product.totalPrice}
              tax={product.totalTax}
              billingUnit={customer.billingUnit}
              items={[
                {
                  label: product.name,
                  value: String(product.price.premiumAfterTax),
                  oldValue: String(product.oldPrice?.premiumAfterTax),
                  icon: product.icon,
                },
                ...(addons?.flatMap((addon) => ({
                  label: addon.title,
                  value: addon.price?.toString() ?? '0',
                  icon: addon.icon,
                  isHalf: addon.isHalf,
                })) ?? []),
              ]}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col area-page-sidebar">
        <Typography
          variant="display6"
          weight="medium"
          className="inline-block mb-24"
        >
          Geselecteerde klant
        </Typography>
        <CustomerInformation customer={customer} />
        <form
          className="mt-auto flex flex-col gap-y-24"
          onSubmit={(event) => {
            event.preventDefault();
            form.handleSubmit();
          }}
        >
          <form.AppForm>
            <form.AppField name="effectiveDate">
              {(field) => (
                <div>
                  <field.DateField
                    label="Ingangsdatum"
                    includePicker
                    minValue={new Date(mutationCart.minimum_effective_date)}
                    {...field}
                  />
                </div>
              )}
            </form.AppField>
            <Button
              type="submit"
              iconRight={IconNavigator}
              intent="cta"
              className="w-full justify-center"
              isDisabled={isPending}
            >
              Wijziging indienen
            </Button>
          </form.AppForm>
        </form>
      </div>
    </div>
  );
}
